import type { Order, OrderId } from '@dx-private/dxchart5-react/dist/chart/model/trading/order.model'
import type { Position } from '@dx-private/dxchart5-react/dist/chart/model/trading/position.model'
import type { PositionProvider } from '@dx-private/dxchart5-react/dist/providers/trading/position.provider'
import { OperationStore, selectOpenPositionsBySymbol } from '../store'
import { tradeDisplayLog } from '../debug/browser-logging'
import type { MergedSymbolInfo } from './dx-trading-mappers'
import { openPositionToDxPosition } from './dx-trading-adapter'
import { blockReadonlyTradingMutation } from './readonly-trading'

export class TournamentPositionProvider implements PositionProvider {
  private observerMap: Map<string, Set<{ callback: (positions: Position[]) => void }>> = new Map()
  private unsubscribeStoreFn?: () => void
  private selectedOperationId: string | null = null
  private hiddenByUser = false
  private lastLoggedPositionSignatureBySymbol = new Map<string, string>()

  constructor(
    private store: OperationStore,
    private symbolFullNames: Record<string, string>,
    private mergedSymbols: Record<string, MergedSymbolInfo>
  ) {}

  observePositions(symbol: string, dataCallback: (positions: Position[]) => void): () => void {
    const fullName = this.toFullSymbol(symbol)

    if (!this.observerMap.has(fullName)) {
      this.observerMap.set(fullName, new Set())
    }

    const observer = { callback: dataCallback }
    this.observerMap.get(fullName)!.add(observer)

    if (!this.unsubscribeStoreFn) {
      this.attachStoreListener()
    }

    this.notifyPositions(fullName)

    return () => {
      const observers = this.observerMap.get(fullName)
      if (observers) {
        observers.delete(observer)
        if (observers.size === 0) {
          this.observerMap.delete(fullName)
        }
      }

      if (this.observerMap.size === 0) {
        this.detachStoreListener()
      }
    }
  }

  async closePosition(symbol: string, id: string, _position?: Position): Promise<void> {
    blockReadonlyTradingMutation('close-position', undefined, {
      symbol: this.toBackendSymbol(symbol),
      positionId: id,
    })
  }

  async closePositionWithOcoOrders(
    symbol: string,
    parentPositionId: Position['id'],
    _orders: [Order | undefined, Order | undefined],
    _position?: Position
  ): Promise<[OrderId, OrderId]> {
    return blockReadonlyTradingMutation('close-position-with-oco-orders', ['' as OrderId, '' as OrderId], {
      symbol: this.toBackendSymbol(symbol),
      parentPositionId,
    })
  }

  setVisibleSelection(selectedOperationId: string | null, hiddenByUser: boolean): void {
    const nextSelectedOperationId = selectedOperationId && selectedOperationId.length > 0
      ? selectedOperationId
      : null
    const nextHiddenByUser = Boolean(hiddenByUser)

    tradeDisplayLog('position-provider setVisibleSelection called', {
      previousSelectedOperationId: this.selectedOperationId,
      nextSelectedOperationId,
      previousHiddenByUser: this.hiddenByUser,
      nextHiddenByUser,
    })

    if (
      this.selectedOperationId === nextSelectedOperationId &&
      this.hiddenByUser === nextHiddenByUser
    ) {
      return
    }

    this.selectedOperationId = nextSelectedOperationId
    this.hiddenByUser = nextHiddenByUser
    this.notifyAllPositionObservers()
  }

  private attachStoreListener(): void {
    this.unsubscribeStoreFn = this.store.subscribe(() => {
      Array.from(this.observerMap.keys()).forEach((symbol) => {
        this.notifyPositions(symbol)
      })
    })
  }

  private detachStoreListener(): void {
    if (this.unsubscribeStoreFn) {
      this.unsubscribeStoreFn()
      this.unsubscribeStoreFn = undefined
    }
  }

  private notifyPositions(symbol: string): void {
    try {
      const { positions, diagnostics } = this.resolvePositions(symbol)

      const logPayload = positions.map((position) => ({
        id: position.id,
        side: position.side,
        quantity: position.quantity,
        price: position.price,
        hasStopLoss: Boolean(position.protectionOrderIds?.[0]),
        hasTakeProfit: Boolean(position.protectionOrderIds?.[1]),
      }))
      const logSignature = JSON.stringify(logPayload)
      if (this.lastLoggedPositionSignatureBySymbol.get(symbol) !== logSignature) {
        this.lastLoggedPositionSignatureBySymbol.set(symbol, logSignature)
        tradeDisplayLog('position-provider emitted positions', {
          symbol,
          hiddenByUser: diagnostics.hiddenByUser,
          selectedOperationId: diagnostics.selectedOperationId,
          authoritativeOperationIds: diagnostics.authoritativeOperationIds,
          positions: logPayload,
        })
      }

      const observers = this.observerMap.get(symbol)
      if (observers) {
        Array.from(observers).forEach((obs) => {
          obs.callback(positions)
        })
      }
    } catch (error) {
      console.error('Error notifying positions:', error)
    }
  }

  private toFullSymbol(symbol: string): string {
    return this.symbolFullNames[symbol] || symbol
  }

  private toBackendSymbol(symbol: string): string {
    if (this.symbolFullNames[symbol]) {
      return symbol
    }

    for (const [backendSymbol, fullSymbol] of Object.entries(this.symbolFullNames)) {
      if (fullSymbol === symbol) {
        return backendSymbol
      }
    }

    return symbol
  }

  private notifyAllPositionObservers(): void {
    Array.from(this.observerMap.keys()).forEach((symbol) => {
      this.notifyPositions(symbol)
    })
  }

  getReadonlyDiagnostics(symbol: string): {
    backendSymbol: string
    hiddenByUser: boolean
    selectedOperationId: string | null
    authoritativeOperationIds: string[]
    emittedPositionIds: string[]
  } {
    const { positions, diagnostics } = this.resolvePositions(symbol)

    return {
      ...diagnostics,
      emittedPositionIds: positions.map((position) => position.id),
    }
  }

  private resolvePositions(symbol: string): {
    positions: Position[]
    diagnostics: {
      backendSymbol: string
      hiddenByUser: boolean
      selectedOperationId: string | null
      authoritativeOperationIds: string[]
    }
  } {
    const state = this.store.getState()
    const backendSymbol = this.toBackendSymbol(symbol)
    const hideAllPositions = this.hiddenByUser && this.selectedOperationId === null
    const openOps = hideAllPositions ? [] : selectOpenPositionsBySymbol(state, backendSymbol)
    const latestPrices = state.latestPricesBySymbol
    const selectedOperationId = this.selectedOperationId

    return {
      positions: openOps.map((op) => {
        const position = openPositionToDxPosition(op, this.mergedSymbols, latestPrices)
        const isSelected = selectedOperationId !== null && position.id === selectedOperationId

        // Only expose TP/SL controls for the selected operation.
        // This keeps non-selected operations' protections hidden/dimmed by omission.
        if (!isSelected && 'protectionOrderIds' in position) {
          delete position.protectionOrderIds
        }

        return position
      }),
      diagnostics: {
        backendSymbol,
        hiddenByUser: this.hiddenByUser,
        selectedOperationId,
        authoritativeOperationIds: openOps.map((operation) => operation.order.id),
      },
    }
  }
}
